/* eslint-disable max-lines */
import { Pill, Text } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { API_TYPES } from "@src/appV2/api";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { augmentYearsOfExperienceWithWorkerLicenses } from "@src/appV2/redesign/PlacementCandidate/utils";
import { useCreateWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useCreateWorkerFavoritePlacement";
import { useDeleteWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useDeleteWorkerFavoritePlacement";
import { usePayTransparencyRates } from "@src/appV2/redesign/Placements/lib/usePayTransparencyRates";
import { constructPlacementDataFromWorkerFavoriteResponse } from "@src/appV2/redesign/Placements/utils/worker-favorite-placements";
import { type Worker } from "@src/appV2/Worker/api/types";
import { differenceInDays, parseISO } from "date-fns";
import { Fragment } from "react";

import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import {
  transformResponseIntoInterview,
  useFetchPaginatedInterviews,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import {
  transformResponseIntoPlacement,
  useFetchPaginatedPlacements,
} from "../api/useFetchPaginatedPlacements";
import {
  EASY_SCHEDULING_BOOKING_DEADLINE_DAYS,
  useGetEasySchedulingPlacements,
} from "../api/useGetEasySchedulingPlacements";
import { useGetWorkerFavoritePlacements } from "../api/useGetWorkerFavoritePlacements";
import {
  type GetPlacementsForWorkplaceQuery,
  PLACEMENT_STATUS_MAP,
  PlacementSortOption,
} from "../types/fetchPlacements.schema";
import { placementWithDetailsToData } from "../utils/placement-data";
import { AppliedPlacementCard } from "./AppliedPlacementCard/AppliedPlacementCard";
import { InterviewAttendanceFeedbackCard } from "./InterviewAttendanceFeedback/InterviewAttendanceFeedbackCard";
import { InterviewsAccordionSection } from "./InterviewsAccordionSection/InterviewsAccordionSection";
import { PendingInterviewsSection } from "./PendingInterviewsSection/PendingInterviewsSection";
import { PlacementsAccordionSectionV3 } from "./PlacementAccordionSection/PlacementsAccordionSectionV3";
import { PlacementCardV2 } from "./PlacementCard/PlacementCardV2";
import { useGetPlacementFilters } from "./PlacementDetailsPage/hooks/useGetPlacementFilters";
import { SentApplicationsAccordionSection } from "./SentApplicationsAccordionSection/SentApplicationsAccordionSection";

interface AppliedPlacementListViewProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  showApplications: boolean;
}

export function AppliedPlacementListView(props: AppliedPlacementListViewProps) {
  const { placementCandidate, worker, filter, showApplications } = props;

  const {
    data: easySchedulingData,
    isLoading: isEasySchedulingLoading,
    fetchNextPage: fetchNextEasySchedulingPage,
    hasNextPage: hasNextEasySchedulingPage,
    isFetchingNextPage: isFetchingNextEasySchedulingPage,
  } = useGetEasySchedulingPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      shouldLogEvent: false,
      additionalFilters: {
        includeClosedPlacementsKeepingInterviewsAndCandidates: true,
      },
    },
    {
      enabled: isDefined(worker.userId) && isDefined(placementCandidate.id),
    }
  );

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const allEasySchedulingPlacements = (easySchedulingData?.pages ?? []).flatMap((page) =>
    page.data.map((placement) =>
      transformResponseIntoPlacement(placement, page.included, credentialRequirementConfiguration)
    )
  );

  const easySchedulingPlacements = allEasySchedulingPlacements.filter((placement) => {
    const application = placement.applications?.[0];
    if (!application?.attributes.updatedAt) {
      return true;
    }

    const updatedDate = parseISO(application.attributes.updatedAt);
    const now = new Date();
    const daysSinceUpdated = differenceInDays(now, updatedDate);

    return daysSinceUpdated <= EASY_SCHEDULING_BOOKING_DEADLINE_DAYS;
  });

  const { data: favoritePlacementsData, isLoading: isFavoritePlacementsLoading } =
    useGetWorkerFavoritePlacements(
      {
        workerId: worker.userId,
      },
      {
        enabled: Boolean(isDefined(worker.userId)),
      }
    );

  const {
    isLoading: isLoadingInterviews,
    data: interviewsData,
    hasNextPage: hasNextPageInterviews,
    isFetchingNextPage: isFetchingNextInterviewsPage,
    fetchNextPage: fetchNextInterviewsPage,
  } = useFetchPaginatedInterviews(
    {
      filter: {},
      sort: "statusBookedFirst,-start",
      include: "placementCandidateBan",
    },
    {
      enabled: isDefined(worker.userId) && showApplications,
    }
  );
  const interviews = (interviewsData?.pages ?? []).flatMap((page) =>
    page.data.map((interview) => ({
      ...transformResponseIntoInterview(interview, page.included ?? [], worker.tmz ?? ""),
      start: new Date(interview.attributes.start),
      end: new Date(interview.attributes.end),
    }))
  );

  const placementFilters = useGetPlacementFilters({
    filter,
    showApplications,
    worker,
    hasInterviews: false,
    enabled: isDefined(placementCandidate),
  });

  const {
    data: placementsData,
    isLoading: isPlacementsLoading,
    hasNextPage: hasNextPagePlacements,
    isFetchingNextPage: isFetchingNextPlacementsPage,
    fetchNextPage: fetchNextPlacementsPage,
  } = useFetchPaginatedPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      filter: placementFilters,
      shouldLogEvent: true,
      sort: PlacementSortOption.APPLICATIONS_APPLIED_DATE_DESC,
    },
    { enabled: isDefined(worker.userId) && showApplications }
  );

  const placements = (placementsData?.pages ?? []).flatMap((placementPage, pageIndex) => {
    return placementPage.data.map((placement, placementIndex) => {
      const placementWithDetails = transformResponseIntoPlacement(
        placement,
        placementPage.included,
        credentialRequirementConfiguration
      );
      return {
        placementWithDetails,
        pageIndex,
        placementIndex,
      };
    });
  });

  const { getCachedPayRatesForPlacement, isLoading: isPayDataLoading } = usePayTransparencyRates();

  const { mutateAsync: favoritePlacement, isLoading: isFavoritingPlacement } =
    useCreateWorkerFavoritePlacement();

  const { mutateAsync: unfavoritePlacement, isLoading: isUnfavoritingPlacement } =
    useDeleteWorkerFavoritePlacement();

  return (
    <Stack direction="column" spacing={5} sx={{ pb: 10 }}>
      <InterviewAttendanceFeedbackCard placementCandidate={placementCandidate} />

      {easySchedulingPlacements.length > 0 && !isEasySchedulingLoading && (
        <PlacementsAccordionSectionV3
          initialExpanded
          title={
            <Stack alignItems="center" direction="row" spacing={2}>
              <Text variant="h5">Pending Interview Invites</Text>
              {easySchedulingPlacements.length > 0 ? (
                <Pill
                  label={easySchedulingPlacements.length}
                  size="small"
                  sx={{
                    backgroundColor: (theme) => theme.palette.error.main,
                    color: (theme) => theme.palette.error.contrastText,
                    padding: 0,
                    minHeight: 20,
                    minWidth: 20,
                    height: "max-content",
                    borderRadius: 10,
                  }}
                />
              ) : undefined}
            </Stack>
          }
          isLoading={isEasySchedulingLoading}
          hasNextPage={hasNextEasySchedulingPage ?? false}
          isFetchingNextPage={isFetchingNextEasySchedulingPage}
          emptyText={
            <Text variant="body1" sx={{ textAlign: "center" }}>
              You have no pending interview invites at this time.
            </Text>
          }
          totalPlacementsCount={easySchedulingPlacements.length}
          placementIds={easySchedulingPlacements.map((p) => p.id)}
          renderPlacementCard={(placementId, index) => {
            const placement = easySchedulingPlacements.find((p) => p.id === placementId);

            if (!isDefined(placement)) {
              return null;
            }

            return (
              <Fragment key={placement.id}>
                <AppliedPlacementCard
                  placement={placementWithDetailsToData(
                    placement,
                    credentialRequirementConfiguration
                  )}
                  placementCandidate={placementCandidate}
                  pageIndex={0}
                  placementIndex={index}
                  worker={worker}
                  source="easy_scheduling_placements"
                />
              </Fragment>
            );
          }}
          onLoadMore={() => {
            void fetchNextEasySchedulingPage();
          }}
        />
      )}

      <InterviewsAccordionSection
        interviews={interviews}
        isLoadingInterviews={isLoadingInterviews}
        hasNextPageInterviews={hasNextPageInterviews ?? false}
        isFetchingNextInterviewsPage={isFetchingNextInterviewsPage}
        showApplications={showApplications}
        placementCandidateId={placementCandidate.id}
        workerTimeZone={worker.tmz}
        onLoadMore={() => {
          void fetchNextInterviewsPage();
        }}
      />

      <PendingInterviewsSection placementCandidate={placementCandidate} worker={worker} />

      <PlacementsAccordionSectionV3
        title="Saved Jobs"
        isLoading={isFavoritePlacementsLoading}
        initialExpanded={(favoritePlacementsData?.included?.length ?? 0) > 0}
        hasNextPage={false}
        isFetchingNextPage={false}
        emptyText={
          <Text variant="body1" sx={{ textAlign: "center" }}>
            You have not saved any jobs to your favorites yet!{" "}
          </Text>
        }
        totalPlacementsCount={
          favoritePlacementsData?.included?.filter(
            (included) => included.type === API_TYPES.placement
          ).length ?? 0
        }
        placementIds={
          favoritePlacementsData?.included
            .filter((included) => included.type === API_TYPES.placement)
            .sort((a, b) => {
              const aIsOpen = a.attributes.status === PLACEMENT_STATUS_MAP.OPEN ? 0 : 1;
              const bIsOpen = b.attributes.status === PLACEMENT_STATUS_MAP.OPEN ? 0 : 1;
              return aIsOpen - bIsOpen;
            })
            .map((placement) => placement.id) ?? []
        }
        renderPlacementCard={(placementId, index) => {
          if (isNil(favoritePlacementsData)) {
            return null;
          }

          const placement = favoritePlacementsData.included
            .filter((included) => included.type === API_TYPES.placement)
            .find((placement) => placement.id === placementId);

          if (!isDefined(placement)) {
            return null;
          }

          const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
            placementCandidate.yearsOfExperienceByQualification,
            worker.licenses
          );

          const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
            {
              id: placement.id,
              workplaceId: placement.relationships.workplace.data.id,
              workerTypes: placement.attributes.workerTypes,
              jobShifts: placement.attributes.jobShifts,
              transparencyPayRates: placement.attributes.transparencyPayRates,
              attributes: { payRange: placement.attributes.payRange },
            },
            yearsOfExperienceByQualification,
            false,
            placementCandidate.payAndScheduleFormData?.benefits?.combined
          );

          const workerFavoritePlacementId = favoritePlacementsData.data.find(
            (fav) => fav.relationships.placement.data.id === placement.id
          )?.id;

          return (
            <PlacementCardV2
              workerQualifications={
                new Set(worker.licensesData.map((license) => license.qualification))
              }
              placement={constructPlacementDataFromWorkerFavoriteResponse(
                placement,
                favoritePlacementsData,
                credentialRequirementConfiguration
              )}
              placementCandidateId={placementCandidate.id}
              isRestricted={placementCandidate.isRestricted ?? false}
              pageIndex={0}
              placementIndex={index}
              loadingPayData={isPayDataLoading}
              minMaxPayEstimate={minMaxPayEstimate}
              source="favorite_placements"
              ongoingFavoritingRequest={isFavoritingPlacement || isUnfavoritingPlacement}
              onFavoriteToggle={async (newFavoriteStatus) => {
                if (isFavoritingPlacement || isUnfavoritingPlacement) {
                  return;
                }

                if (newFavoriteStatus) {
                  await favoritePlacement({
                    workerId: worker.userId,
                    placementId: placement.id,
                    source: "favorite_placements_list",
                  });
                } else if (isDefined(workerFavoritePlacementId)) {
                  await unfavoritePlacement({
                    workerId: worker.userId,
                    workerFavoritePlacementId,
                    placementId: placement.id,
                    source: "favorite_placements_list",
                  });
                }
              }}
            />
          );
        }}
      />

      {placements.length > 0 && !isPlacementsLoading && (
        <SentApplicationsAccordionSection
          placements={placements}
          isLoadingPlacements={isPlacementsLoading}
          hasNextPagePlacements={hasNextPagePlacements ?? false}
          isFetchingNextPlacementsPage={isFetchingNextPlacementsPage}
          showApplications={showApplications}
          placementCandidateId={placementCandidate.id}
          workerTimeZone={worker.tmz}
          onLoadMore={() => {
            void fetchNextPlacementsPage();
          }}
        />
      )}
    </Stack>
  );
}
/* eslint-enable max-lines */
